import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import TabPanel from '../../components/tabs/TabPanel';
import useBeerIngestion from '../../hooks/useBeerIngestion';
import useBeerIngestionReviews from '../../hooks/useBeerIngestionReviews';
import useBeerIngestionFailures from '../../hooks/useBeerIngestionFailures';
import useBeerProfileProcessingFailures from '../../hooks/useBeerProfileProcessingFailures';
import useNhProfileProcessingPulse from '../../hooks/useNhProfileProcessingPulse';
import useBeerIngestionScrollRestore from '../../hooks/useBeerIngestionScrollRestore';
import useBeerIngestionPilotScoreboard from '../../hooks/useBeerIngestionPilotScoreboard';
import BreweryBeerSourceFormDialog from '../brewery/BreweryBeerSourceFormDialog';
import {
  BEER_INGESTION_DEFAULT_STATE,
  BEER_INGESTION_STATUS_FILTER_ALL,
} from '../../utils/callableBeerConstants';
import {
  filterBeerIngestionRows,
  getAddSourcePrefillUrl,
} from '../../utils/brewery/beerIngestionStatus';
import { captureBeerIngestionScrollPosition } from '../../utils/brewery/beerIngestionScrollPosition';
import {
  BEER_INGESTION_MONITORING_FILTER_ALL,
  summarizeBeerIngestionMonitoring,
} from '../../utils/brewery/beerIngestionMonitoring';
import { PROFILE_PROCESSING_FILTER_ALL } from '../../utils/brewery/beerProfileProcessingFailures';
import {
  captureBeerIngestionCheckNow,
  captureBeerIngestionEnableMonitoring,
  captureBeerIngestionMonitoringFilter,
  captureBeerIngestionPause,
  captureBeerIngestionRecreateMonitor,
  captureBeerIngestionResume,
  captureProfileProcessingFailuresFilter,
} from '../../utils/beerIngestionAnalytics';
import {
  BeerIngestionFilterBar,
  BeerIngestionProgressBanner,
  BeerIngestionTable,
  ExcludeBreweryDialog,
  NoSuitableSourceDialog,
  RecreateMonitorDialog,
} from './beerIngestionPanels';
import BeerIngestionReviewInbox from './BeerIngestionReviewInbox';
import BeerIngestionFailuresInbox from './BeerIngestionFailuresInbox';
import BeerProfileProcessingFailuresInbox from './BeerProfileProcessingFailuresInbox';
import BeerIngestionStatusPanel from './BeerIngestionStatusPanel';
import states from '../../shared/states/usStates.json';

const BEER_INGESTION_TAB_SOURCES = 0;
const BEER_INGESTION_TAB_REVIEW = 1;
const BEER_INGESTION_TAB_FAILURES = 2;
const BEER_INGESTION_TAB_PROFILE = 3;
const BEER_INGESTION_TAB_STATUS = 4;

const LOCAL_STORAGE_KEY = 'beerIngestion.usState';
const PAGE_CONTENT_SX = {
  mt: 2,
  mb: 4,
  px: { xs: 2, sm: 3 },
};

function readStoredStateCode() {
  try {
    const stored = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored && states.some((state) => state.code === stored)) {
      return stored;
    }
  } catch {
    // ignore private-mode / unavailable storage
  }
  return BEER_INGESTION_DEFAULT_STATE;
}

function StateSelector({ usState, onChange }) {
  const stateMenuItems = useMemo(
    () =>
      states.map((state) => (
        <MenuItem value={state.code} key={state.code}>
          {state.name}
        </MenuItem>
      )),
    [],
  );

  return (
    <FormControl sx={{ minWidth: 280 }}>
      <InputLabel id="beer-ingestion-state-label">State</InputLabel>
      <Select
        labelId="beer-ingestion-state-label"
        label="State"
        value={usState}
        onChange={onChange}
      >
        {stateMenuItems}
      </Select>
    </FormControl>
  );
}

StateSelector.propTypes = {
  usState: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

function readInitialTab(searchParams) {
  const tabParam = searchParams.get('tab');
  if (tabParam === 'failures') {
    return BEER_INGESTION_TAB_FAILURES;
  }
  if (tabParam === 'profile' || tabParam === 'profile-processing') {
    return BEER_INGESTION_TAB_PROFILE;
  }
  if (tabParam === 'review') {
    return BEER_INGESTION_TAB_REVIEW;
  }
  if (tabParam === 'status') {
    return BEER_INGESTION_TAB_STATUS;
  }
  return BEER_INGESTION_TAB_SOURCES;
}

function BeerIngestionPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [usState, setUsState] = useState(readStoredStateCode);
  const [statusFilter, setStatusFilter] = useState(BEER_INGESTION_STATUS_FILTER_ALL);
  const [monitoringFilter, setMonitoringFilter] = useState(
    BEER_INGESTION_MONITORING_FILTER_ALL,
  );
  const [hasWebsite, setHasWebsite] = useState(false);
  const [showExcluded, setShowExcluded] = useState(false);
  const [addSourceRow, setAddSourceRow] = useState(null);
  const [noSourceRow, setNoSourceRow] = useState(null);
  const [excludeRow, setExcludeRow] = useState(null);
  const [recreateRow, setRecreateRow] = useState(null);
  const [profileDisposition, setProfileDisposition] = useState('open');
  const [profileCategory, setProfileCategory] = useState(PROFILE_PROCESSING_FILTER_ALL);
  const [profileStage, setProfileStage] = useState(PROFILE_PROCESSING_FILTER_ALL);
  const tab = useMemo(() => readInitialTab(searchParams), [searchParams]);
  const profileTabActive = tab === BEER_INGESTION_TAB_PROFILE;

  const {
    rows,
    summary,
    loading,
    error,
    actionError,
    actionLoading,
    pendingAction,
    classifyBrewery,
    excludeBrewery,
    addOrUpdateSource,
    markTrusted,
    updateSourceStatus,
    checkSourceNow,
    recreateSourceMonitor,
    startSourceMonitoring,
    enableNhMonitoring,
  } = useBeerIngestion(usState);
  const {
    rows: reviewRows,
    summary: reviewSummary,
    loading: reviewLoading,
    error: reviewError,
  } = useBeerIngestionReviews(usState);
  const {
    rows: failureRows,
    summary: failureSummary,
    loading: failuresLoading,
    error: failuresError,
    actionError: failuresActionError,
    actionLoading: failuresActionLoading,
    pendingItemId: failuresPendingItemId,
    refresh: refreshFailures,
    resolveFailure,
    retryFailure,
  } = useBeerIngestionFailures(usState);
  const {
    rows: profileFailureRows,
    summary: profileFailureSummary,
    loading: profileFailuresLoading,
    error: profileFailuresError,
    actionError: profileFailuresActionError,
    actionLoading: profileFailuresActionLoading,
    pendingItemId: profileFailuresPendingItemId,
    refresh: refreshProfileFailures,
    resolveFailure: resolveProfileFailure,
    retryFailure: retryProfileFailure,
  } = useBeerProfileProcessingFailures({
    enabled: true,
    disposition: profileDisposition,
    category: profileCategory,
    failureStage: profileStage,
  });
  const {
    pulse: profilePulse,
    trustedBreweryCount: profileTrustedBreweryCount,
    loading: profilePulseLoading,
    error: profilePulseError,
  } = useNhProfileProcessingPulse({
    enabled: profileTabActive,
  });
  const {
    scoreboard: pilotScoreboard,
    loading: scoreboardLoading,
    error: scoreboardError,
  } = useBeerIngestionPilotScoreboard(usState, tab === BEER_INGESTION_TAB_STATUS);

  useBeerIngestionScrollRestore({
    hasRows: rows.length > 0,
    loading,
    usState,
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(LOCAL_STORAGE_KEY, usState);
    } catch {
      // ignore write errors
    }
  }, [usState]);

  const filteredRows = useMemo(
    () =>
      filterBeerIngestionRows(rows, {
        status: statusFilter,
        hasWebsite,
        showExcluded,
        monitoringFilter,
      }),
    [hasWebsite, monitoringFilter, rows, showExcluded, statusFilter],
  );

  const monitoringSummary = useMemo(
    () => summarizeBeerIngestionMonitoring(rows),
    [rows],
  );

  const emptyMessage = useMemo(() => {
    if (rows.length === 0) {
      return 'No open breweries in this state.';
    }
    return 'No breweries match the current filters.';
  }, [rows.length]);

  const handleStateChange = useCallback((event) => {
    setUsState(event.target.value);
    window.scrollTo(0, 0);
  }, []);

  const handleTabChange = useCallback((unusedEvent, nextTab) => {
    const nextParams = new window.URLSearchParams(searchParams);
    if (nextTab === BEER_INGESTION_TAB_FAILURES) {
      nextParams.set('tab', 'failures');
    } else if (nextTab === BEER_INGESTION_TAB_PROFILE) {
      nextParams.set('tab', 'profile');
    } else if (nextTab === BEER_INGESTION_TAB_REVIEW) {
      nextParams.set('tab', 'review');
    } else if (nextTab === BEER_INGESTION_TAB_STATUS) {
      nextParams.set('tab', 'status');
    } else {
      nextParams.delete('tab');
    }
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleProfileDispositionChange = useCallback((event) => {
    const nextValue = event.target.value;
    setProfileDisposition(nextValue);
    captureProfileProcessingFailuresFilter('disposition', nextValue);
  }, []);

  const handleProfileCategoryChange = useCallback((event) => {
    const nextValue = event.target.value;
    setProfileCategory(nextValue);
    captureProfileProcessingFailuresFilter('category', nextValue);
  }, []);

  const handleProfileStageChange = useCallback((event) => {
    const nextValue = event.target.value;
    setProfileStage(nextValue);
    captureProfileProcessingFailuresFilter('failureStage', nextValue);
  }, []);

  const handleStatusFilterChange = useCallback((event) => {
    setStatusFilter(event.target.value);
  }, []);

  const handleMonitoringFilterChange = useCallback((event) => {
    const nextFilter = event.target.value;
    setMonitoringFilter(nextFilter);
    captureBeerIngestionMonitoringFilter(nextFilter);
  }, []);

  const handleHasWebsiteChange = useCallback((event) => {
    setHasWebsite(event.target.checked);
  }, []);

  const handleShowExcludedChange = useCallback((event) => {
    setShowExcluded(event.target.checked);
  }, []);

  const handleEnableNhMonitoring = useCallback(() => {
    captureBeerIngestionEnableMonitoring({ stateCode: 'NH' });
    enableNhMonitoring().catch(() => undefined);
  }, [enableNhMonitoring]);

  const handleCloseAddSource = useCallback(() => {
    setAddSourceRow(null);
  }, []);

  const handleCloseNoSource = useCallback(() => {
    setNoSourceRow(null);
  }, []);

  const handleCloseExclude = useCallback(() => {
    setExcludeRow(null);
  }, []);

  const handleCloseRecreate = useCallback(() => {
    setRecreateRow(null);
  }, []);

  const handleAddOrUpdateSource = useCallback(
    async (payload) => {
      if (!addSourceRow) {
        return;
      }
      await addOrUpdateSource(addSourceRow.breweryId, payload);
      setAddSourceRow(null);
    },
    [addOrUpdateSource, addSourceRow],
  );

  const handleNoSourceSubmit = useCallback(
    async ({ reason, notes }) => {
      if (!noSourceRow) {
        return;
      }
      try {
        await classifyBrewery(noSourceRow.breweryId, {
          status: 'no_suitable_source',
          reason,
          notes,
        });
        setNoSourceRow(null);
      } catch {
        // actionError is surfaced by useBeerIngestion
      }
    },
    [classifyBrewery, noSourceRow],
  );

  const handleExcludeSubmit = useCallback(
    async ({ reason }) => {
      if (!excludeRow) {
        return;
      }
      try {
        await excludeBrewery(excludeRow.breweryId, {
          excluded: true,
          reason,
        });
        setExcludeRow(null);
      } catch {
        // actionError is surfaced by useBeerIngestion
      }
    },
    [excludeBrewery, excludeRow],
  );

  const handleRecreateConfirm = useCallback(async () => {
    const breweryId = recreateRow?.breweryId;
    const sourceId = recreateRow?.trustedSource?.id;
    if (!breweryId || !sourceId) {
      return;
    }
    try {
      captureBeerIngestionRecreateMonitor(breweryId, sourceId);
      await recreateSourceMonitor(breweryId, sourceId);
      setRecreateRow(null);
    } catch {
      // actionError is surfaced by useBeerIngestion
    }
  }, [recreateRow, recreateSourceMonitor]);

  const handleRowActionClick = useCallback(
    (event) => {
      const action = event.currentTarget.getAttribute('data-action');
      const breweryId = event.currentTarget.getAttribute('data-brewery-id');
      const sourceId = event.currentTarget.getAttribute('data-source-id');
      const row = rows.find((item) => item.breweryId === breweryId);
      if (!row) {
        return;
      }
      if (action === 'open') {
        captureBeerIngestionScrollPosition({ usState, breweryId });
        return;
      }
      if (action === 'add-source') {
        setAddSourceRow(row);
        return;
      }
      if (action === 'trust' && sourceId) {
        markTrusted(breweryId, sourceId).catch(() => undefined);
        return;
      }
      if (action === 'no-source') {
        setNoSourceRow(row);
        return;
      }
      if (action === 'clear-no-source') {
        classifyBrewery(breweryId, { status: 'needs_source' }).catch(() => undefined);
        return;
      }
      if (action === 'exclude') {
        setExcludeRow(row);
        return;
      }
      if (action === 'include') {
        excludeBrewery(breweryId, { excluded: false }).catch(() => undefined);
        return;
      }
      if (action === 'check-now' && sourceId) {
        captureBeerIngestionCheckNow(breweryId, sourceId);
        checkSourceNow(breweryId, sourceId).catch(() => undefined);
        return;
      }
      if (action === 'pause' && sourceId) {
        captureBeerIngestionPause(breweryId, sourceId);
        updateSourceStatus(breweryId, sourceId, 'paused').catch(() => undefined);
        return;
      }
      if (action === 'resume' && sourceId) {
        captureBeerIngestionResume(breweryId, sourceId);
        updateSourceStatus(breweryId, sourceId, 'active').catch(() => undefined);
        return;
      }
      if (action === 'start-monitoring' && sourceId) {
        captureBeerIngestionEnableMonitoring({ sourceId, breweryId });
        startSourceMonitoring(breweryId, sourceId).catch(() => undefined);
        return;
      }
      if (action === 'recreate-monitor') {
        setRecreateRow(row);
      }
    },
    [
      checkSourceNow,
      classifyBrewery,
      excludeBrewery,
      markTrusted,
      rows,
      startSourceMonitoring,
      updateSourceStatus,
      usState,
    ],
  );

  return (
    <DashboardLayout title="Beer ingestion">
      <Box sx={PAGE_CONTENT_SX}>
        <Stack spacing={2}>
          <Typography variant="body1" color="text.secondary">
            Classify official beer sources for open breweries in the selected state.
            Trusted official sources are checked weekly (Eastern, staggered). Unchanged
            pages are not extracted. Aggregators stay visible but cannot be trusted.
            Target: 80% trusted official sources.
          </Typography>
          <StateSelector usState={usState} onChange={handleStateChange} />
          <Tabs value={tab} onChange={handleTabChange} aria-label="Beer ingestion workspace">
            <Tab label="Sources" id="simple-tab-0" />
            <Tab
              id="simple-tab-1"
              label={(
                <Badge
                  color="primary"
                  badgeContent={
                    reviewSummary.pendingRuns
                    + reviewSummary.inProgressRuns
                    + reviewSummary.unappliedRuns
                  }
                  max={99}
                >
                  Review
                </Badge>
              )}
            />
            <Tab
              id="simple-tab-2"
              label={(
                <Badge color="error" badgeContent={failureSummary.open} max={99}>
                  Failures
                </Badge>
              )}
            />
            <Tab
              id="simple-tab-3"
              label={(
                <Badge
                  color="error"
                  badgeContent={
                    profileDisposition === 'open' ? profileFailureSummary.open : 0
                  }
                  max={99}
                >
                  Profile processing
                </Badge>
              )}
            />
            <Tab label="Status" id="simple-tab-4" />
          </Tabs>
          <TabPanel value={tab} index={BEER_INGESTION_TAB_SOURCES} disableGutters>
            {usState === 'NH' && monitoringSummary.unmonitoredTrusted > 0 ? (
              <Button
                variant="outlined"
                onClick={handleEnableNhMonitoring}
                disabled={actionLoading}
                sx={{ mb: 2 }}
              >
                Start monitoring for trusted NH sources
              </Button>
            ) : null}
            <BeerIngestionProgressBanner
              summary={summary}
              monitoringSummary={monitoringSummary}
            />
            <BeerIngestionFilterBar
              statusFilter={statusFilter}
              monitoringFilter={monitoringFilter}
              hasWebsite={hasWebsite}
              showExcluded={showExcluded}
              onStatusFilterChange={handleStatusFilterChange}
              onMonitoringFilterChange={handleMonitoringFilterChange}
              onHasWebsiteChange={handleHasWebsiteChange}
              onShowExcludedChange={handleShowExcludedChange}
            />
            <BeerIngestionTable
              rows={filteredRows}
              loading={loading}
              error={error}
              emptyMessage={emptyMessage}
              actionLoading={actionLoading}
              pendingAction={pendingAction}
              onRowActionClick={handleRowActionClick}
            />
          </TabPanel>
          <TabPanel value={tab} index={BEER_INGESTION_TAB_REVIEW} disableGutters>
            <BeerIngestionReviewInbox
              rows={reviewRows}
              loading={reviewLoading}
              error={reviewError}
              stateCode={usState}
            />
          </TabPanel>
          <TabPanel value={tab} index={BEER_INGESTION_TAB_FAILURES} disableGutters>
            <BeerIngestionFailuresInbox
              rows={failureRows}
              summary={failureSummary}
              loading={failuresLoading}
              error={failuresError}
              actionError={failuresActionError}
              actionLoading={failuresActionLoading}
              pendingItemId={failuresPendingItemId}
              onRetry={retryFailure}
              onResolve={resolveFailure}
              onRefresh={refreshFailures}
            />
          </TabPanel>
          <TabPanel value={tab} index={BEER_INGESTION_TAB_PROFILE} disableGutters>
            <BeerProfileProcessingFailuresInbox
              rows={profileFailureRows}
              summary={profileFailureSummary}
              loading={profileFailuresLoading}
              error={profileFailuresError}
              actionError={profileFailuresActionError}
              actionLoading={profileFailuresActionLoading}
              pendingItemId={profileFailuresPendingItemId}
              disposition={profileDisposition}
              category={profileCategory}
              failureStage={profileStage}
              onDispositionChange={handleProfileDispositionChange}
              onCategoryChange={handleProfileCategoryChange}
              onStageChange={handleProfileStageChange}
              onRetry={retryProfileFailure}
              onResolve={resolveProfileFailure}
              onRefresh={refreshProfileFailures}
              pulse={profilePulse}
              trustedBreweryCount={profileTrustedBreweryCount}
              pulseLoading={profilePulseLoading}
              pulseError={profilePulseError}
            />
          </TabPanel>
          <TabPanel value={tab} index={BEER_INGESTION_TAB_STATUS} disableGutters>
            <BeerIngestionStatusPanel
              scoreboard={pilotScoreboard}
              loading={scoreboardLoading}
              error={scoreboardError}
            />
          </TabPanel>
        </Stack>
      </Box>
      <BreweryBeerSourceFormDialog
        open={Boolean(addSourceRow)}
        onClose={handleCloseAddSource}
        breweryId={addSourceRow?.breweryId || ''}
        initialUrl={getAddSourcePrefillUrl(addSourceRow)}
        editingSource={null}
        actionError={actionError}
        actionLoading={actionLoading}
        onAddOrUpdateSource={handleAddOrUpdateSource}
      />
      <NoSuitableSourceDialog
        open={Boolean(noSourceRow)}
        actionError={actionError}
        actionLoading={actionLoading}
        onClose={handleCloseNoSource}
        onSubmit={handleNoSourceSubmit}
      />
      <ExcludeBreweryDialog
        open={Boolean(excludeRow)}
        actionError={actionError}
        actionLoading={actionLoading}
        onClose={handleCloseExclude}
        onSubmit={handleExcludeSubmit}
      />
      <RecreateMonitorDialog
        open={Boolean(recreateRow)}
        breweryName={recreateRow?.name}
        actionError={actionError}
        actionLoading={actionLoading}
        onClose={handleCloseRecreate}
        onConfirm={handleRecreateConfirm}
      />
    </DashboardLayout>
  );
}

export default BeerIngestionPage;
